import React from 'react';
import { Box, Typography, alpha, Tooltip } from '@mui/material';
import { Category } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const SECTOR_COLORS = {
  FINANCIALS: '#3b82f6',
  MATERIALS: '#f59e0b',
  INDUSTRIALS: '#6366f1',
  CONSUMER_DISCRETIONARY: '#ec4899',
  CONSUMER_STAPLES: '#10b981',
  HEALTH_CARE: '#ef4444',
  INFORMATION_TECHNOLOGY: '#06b6d4',
  REAL_ESTATE: '#8b5cf6',
  ENERGY: '#f97316',
  UTILITIES: '#84cc16',
  COMMUNICATION_SERVICES: '#14b8a6',
};

const getSectorColor = (sector) => SECTOR_COLORS[sector] || '#64748b';

const SectorHeatmap = ({ sectorDistribution = [], onSectorClick, selectedSector }) => {
  const { t } = useTranslation();
  if (!sectorDistribution || sectorDistribution.length === 0) return null;

  const total = sectorDistribution.reduce((s, d) => s + d.count, 0);

  return (
    <Box sx={{ mb: 2 }}>
      {/* Compact header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
        <Category sx={{ fontSize: 14, color: '#94a3b8' }} />
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#475569' }}>
          {t('regimeWatchlist.sectorAllocation', 'Sector Allocation')}
        </Typography>
        <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', ml: 0.5 }}>
          {sectorDistribution.length} {t('regimeWatchlist.sectorsLower', 'sectors')} • {total} {t('regimeWatchlist.stocks', 'stocks')}
        </Typography>
        {selectedSector && (
          <Box
            onClick={() => onSectorClick?.(selectedSector)}
            sx={{
              ml: 0.5, px: 0.8, py: 0.15, borderRadius: 2,
              bgcolor: alpha(getSectorColor(selectedSector), 0.1),
              border: `1px solid ${alpha(getSectorColor(selectedSector), 0.2)}`,
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.3,
              '&:hover': { bgcolor: alpha(getSectorColor(selectedSector), 0.2) },
            }}
          >
            <Typography sx={{ fontSize: '0.55rem', fontWeight: 700, color: getSectorColor(selectedSector) }}>
              {selectedSector.replace(/_/g, ' ')}
            </Typography>
            <Typography sx={{ fontSize: '0.6rem', color: '#94a3b8' }}>✕</Typography>
          </Box>
        )}
      </Box>

      {/* Stacked bar (proportional widths) */}
      <Box sx={{ display: 'flex', height: 22, borderRadius: 0.5, overflow: 'hidden', mb: 0.8, border: '1px solid #e5e7eb' }}>
        {sectorDistribution.map((s, i) => {
          const color = getSectorColor(s.sector);
          const pct = (s.count / total) * 100;
          return (
            <Tooltip
              key={i}
              title={
                <Box sx={{ fontSize: '0.65rem' }}>
                  <b>{s.sector}</b> — {s.count} CP ({s.pct}%)<br />
                  Ret5d: {s.avg_ret_5d > 0 ? '+' : ''}{s.avg_ret_5d}% | Ret20d: {s.avg_ret_20d > 0 ? '+' : ''}{s.avg_ret_20d}%<br />
                  RSI: {s.avg_rsi} | Score: {s.avg_score}<br />
                  {s.symbols.join(', ')}
                </Box>
              }
              arrow
            >
              <Box
                onClick={(e) => { e.stopPropagation(); onSectorClick?.(s.sector); }}
                sx={{
                width: `${pct}%`, minWidth: pct > 3 ? 24 : 8,
                bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.3s ease',
                opacity: selectedSector && selectedSector !== s.sector ? 0.35 : 1,
                '&:hover': { opacity: selectedSector && selectedSector !== s.sector ? 0.5 : 0.85 },
                borderRight: i < sectorDistribution.length - 1 ? '1px solid white' : 'none',
              }}>
                {pct >= 12 && (
                  <Typography sx={{ fontSize: '0.5rem', fontWeight: 800, color: 'white', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    {s.sector.replace(/_/g, ' ').slice(0, 8)}
                  </Typography>
                )}
              </Box>
            </Tooltip>
          );
        })}
      </Box>

      {/* Sector detail rows */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 0.5 }}>
        {sectorDistribution.map((s, i) => {
          const color = getSectorColor(s.sector);
          const retColor = s.avg_ret_5d > 2 ? '#10b981' : s.avg_ret_5d > 0 ? '#3b82f6' : '#ef4444';
          return (
            <Box key={i}
              onClick={() => onSectorClick?.(s.sector)}
              sx={{
              display: 'flex', alignItems: 'center', gap: 0.8, py: 0.4, px: 0.8,
              borderRadius: 0.5,
              bgcolor: selectedSector === s.sector ? alpha(color, 0.12) : alpha(color, 0.04),
              border: `1px solid ${selectedSector === s.sector ? color : alpha(color, 0.08)}`,
              cursor: onSectorClick ? 'pointer' : 'default',
              transition: 'all 0.2s ease',
              opacity: selectedSector && selectedSector !== s.sector ? 0.5 : 1,
              '&:hover': onSectorClick ? { bgcolor: alpha(color, 0.08) } : {},
            }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: color, flexShrink: 0 }} />
              <Typography sx={{
                fontSize: '0.62rem', fontWeight: 700, color: '#334155',
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {s.sector.replace(/_/g, ' ')}
              </Typography>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#64748b', flexShrink: 0 }}>
                {s.count}
              </Typography>
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: retColor, fontFamily: 'monospace', flexShrink: 0, minWidth: 38, textAlign: 'right' }}>
                {s.avg_ret_5d > 0 ? '+' : ''}{s.avg_ret_5d}%
              </Typography>
              <Typography sx={{ fontSize: '0.48rem', color: '#94a3b8', flexShrink: 0 }}>
                5d
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SectorHeatmap;
